import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from '../ui/FadeIn';
import { CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const categories = [{
  name: 'Inflamación y Dolores',
  desc: 'Articulaciones, músculos, rigidez, dolor por frío o humedad.',
  count: 30,
  image: 'https://cdn.magicpatterns.com/uploads/csRf8GsgSHvbMkhhRwSpZb/Inflamacion_y_dolores.png'
}, {
  name: 'Digestión',
  desc: 'Gases, hinchazón, acidez, estreñimiento y náuseas.',
  count: 90,
  image: 'https://cdn.magicpatterns.com/uploads/uMajFBUgjUJNvkUtdUpiHe/Digestion.png'
}, {
  name: 'Ansiedad y Sueño',
  desc: 'Insomnio, nervios, estrés y mente acelerada.',
  count: 30,
  image: 'https://cdn.magicpatterns.com/uploads/5pFm9YJc624hbPJ6S5vRr7/ANSIEdad_y_sueno.png'
}, {
  name: 'Defensas y Resfríos',
  desc: 'Gripe, tos, garganta y defensas bajas.',
  count: 60,
  image: 'https://cdn.magicpatterns.com/uploads/o9vYkhfLsAe5ZvAzvEa3xk/Defensas_y_resfrios.png'
}, {
  name: 'Metabolismo y Peso',
  desc: 'Líquidos, azúcar, grasa y metabolismo lento.',
  count: 30,
  image: 'https://cdn.magicpatterns.com/uploads/8X1PyN1zNhkRe1AxgpQUNw/Metabolismo_y_peso.png'
}, {
  name: 'Articulaciones y Huesos',
  desc: 'Artritis, rigidez, desgaste y circulación.',
  count: 30,
  image: 'https://cdn.magicpatterns.com/uploads/evNSvfa4av3uGnveQYVyQA/Articulacion_y_huesos.png'
}, {
  name: 'Piel y Circulación',
  desc: 'Piel seca, manchas, várices y piernas pesadas.',
  count: 30,
  image: 'https://cdn.magicpatterns.com/uploads/idzt2Rou985p4fsmEiq48L/Piel_y_circulacion.png'
}];
export function RecipeCategoriesSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const goTo = useCallback((index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  }, [current]);
  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % categories.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + categories.length) % categories.length);
  }, []);
  // Auto-slide disabled - manual navigation only
  const cat = categories[current];
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0
    })
  };
  return <section className="bg-ancestral-white py-14 md:py-20 text-ancestral-dark" data-id="element-410">
      <div className="container mx-auto px-4" data-id="element-411">
        <div className="max-w-5xl mx-auto" data-id="element-412">
          {/* Header */}
          <FadeIn className="text-center mb-10" data-id="element-413">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-ancestral-green mb-2" data-id="element-414">
              ¿Qué Obtienes Con El Recetario?
            </h2>
            <p className="text-base md:text-lg text-ancestral-dark/60" data-id="element-415">
              300 recetas en 7 categorías, listas para usar desde el día 1
            </p>
          </FadeIn>

          {/* Carousel */}
          <FadeIn delay={0.15} data-id="element-416">
            <div className="relative max-w-sm mx-auto mb-10" data-id="element-417">
              {/* Image + info card */}
              <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg border border-gray-100" data-id="element-418">
                <div className="relative aspect-[4/5] overflow-hidden" data-id="element-419">
                  <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-420">
                    <motion.img key={current} src={cat.image} alt={cat.name} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.35,
                    ease: 'easeInOut'
                  }} className="absolute inset-0 w-full h-full object-cover" data-id="element-421" />
                  </AnimatePresence>

                  {/* Recipe count badge */}
                  <div className="absolute top-4 right-4 bg-ancestral-green backdrop-blur-sm text-ancestral-cream px-3 py-1.5 rounded-full z-10 shadow-md" data-id="element-422">
                    <span className="text-sm font-bold" data-id="element-423">
                      {cat.count} recetas
                    </span>
                  </div>
                </div>

                {/* Text below image */}
                <AnimatePresence mode="wait" data-id="element-424">
                  <motion.div key={current} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.25
                }} className="p-5" data-id="element-425">
                    <h3 className="font-serif font-bold text-xl text-ancestral-green mb-1" data-id="element-426">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-ancestral-dark/60 leading-relaxed" data-id="element-427">
                      {cat.desc}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation arrows */}
              <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 bg-white shadow-md border border-gray-200 rounded-full p-2 hover:bg-gray-50 transition-colors z-20" aria-label="Anterior" data-id="element-428">
                <ChevronLeftIcon className="w-5 h-5 text-ancestral-green" data-id="element-429" />
              </button>
              <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 bg-white shadow-md border border-gray-200 rounded-full p-2 hover:bg-gray-50 transition-colors z-20" aria-label="Siguiente" data-id="element-430">
                <ChevronRightIcon className="w-5 h-5 text-ancestral-green" data-id="element-431" />
              </button>
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mb-10" data-id="element-432">
              {categories.map((_, idx) => <button key={idx} onClick={() => goTo(idx)} className={`rounded-full transition-all duration-300 ${idx === current ? 'w-8 h-2.5 bg-ancestral-green' : 'w-2.5 h-2.5 bg-ancestral-green/25 hover:bg-ancestral-green/40'}`} aria-label={`Ir a ${categories[idx].name}`} data-id="element-433" />)}
            </div>
          </FadeIn>

          {/* Features bar */}
          <FadeIn delay={0.3} data-id="element-434">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-ancestral-dark/70 font-medium" data-id="element-435">
              {['Ingredientes exactos', 'Paso a paso', 'Menos de 10 min', 'Sin efectos secundarios'].map((f, i) => <span key={i} className="flex items-center gap-1.5" data-id="element-436">
                  <CheckCircle2Icon className="w-4 h-4 text-ancestral-gold" data-id="element-437" />
                  {f}
                </span>)}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}